import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { auth } from "../firebaseClient";
import { useLocations } from "../hooks/LocationContext";
import type { StoreLocation } from "../api/locationsApi";
import {
  deleteLocation,
  upsertLocationMultipart, // <-- NEW
} from "../api/locationsApi";

type Draft = Omit<StoreLocation, "id">;

const emptyDraft = (): Draft => ({
  name: "",
  number: "",
  hours: [],
  location: "",
  map: "",
  mapImg: "",
  storeImg: "",
  latitude: null,
  longitude: null,
  cuid: "",
  ruid: "",
  info: "",
});

function toNumOrNull(v: string): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export default function AdminLocationsForm() {
  const { LOCATIONS, loading, error, refresh } = useLocations();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft());

  const [hoursInput, setHoursInput] = useState("");
  const [busy, setBusy] = useState(false);

  // previews
  const [mapPreview, setMapPreview] = useState("");
  const [storePreview, setStorePreview] = useState("");

  // staged files (only uploaded on Save)
  const [mapFile, setMapFile] = useState<File | null>(null);
  const [storeFile, setStoreFile] = useState<File | null>(null);

  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: "success" | "error" | "info" }>(
    { open: false, msg: "", sev: "info" }
  );

  const selected = useMemo(
    () => (LOCATIONS as any[]).find((x) => x.id === selectedId) ?? null,
    [LOCATIONS, selectedId]
  );

  useEffect(() => {
    if (!selected) return;

    setDraft({
      name: selected.name ?? "",
      number: selected.number ?? "",
      hours: Array.isArray(selected.hours) ? selected.hours : [],
      location: selected.location ?? "",
      map: selected.map ?? "",
      mapImg: "",
      storeImg: "",
      latitude: selected.latitude ?? null,
      longitude: selected.longitude ?? null,
      cuid: selected.cuid ?? "",
      ruid: selected.ruid ?? "",
      info: selected.info ?? "",
    });

    // NOTE: your GET returns URLs for these, so these are good for preview
    setMapPreview(selected.mapImg ?? "");
    setStorePreview(selected.storeImg ?? "");

    // clear staged files when switching selections
    setMapFile(null);
    setStoreFile(null);

    setHoursInput("");
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  // cleanup blob URLs
  useEffect(() => {
    return () => {
      if (mapPreview.startsWith("blob:")) URL.revokeObjectURL(mapPreview);
      if (storePreview.startsWith("blob:")) URL.revokeObjectURL(storePreview);
    };
  }, [mapPreview, storePreview]);

  function startNew() {
    setSelectedId(null);
    setDraft(emptyDraft());
    setMapPreview("");
    setStorePreview("");
    setHoursInput("");
    setMapFile(null);
    setStoreFile(null);
  }

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  function addHour() {
    const line = hoursInput.trim();
    if (!line) return;
    setDraft((d) => ({ ...d, hours: [...d.hours, line] }));
    setHoursInput("");
  }

  function removeHour(idx: number) {
    setDraft((d) => ({ ...d, hours: d.hours.filter((_, i) => i !== idx) }));
  }

  async function getTokenOrThrow() {
    const user = auth.currentUser;
    if (!user) throw new Error("Not signed in");
    return await user.getIdToken();
  }

  function validate(d: Draft) {
    if (!d.name.trim()) return "Name is required";
    if (!d.location.trim()) return "Location is required";
    if (!Array.isArray(d.hours)) return "Hours must be a list";
    return null;
  }

  async function onSave() {
    const err = validate(draft);
    if (err) {
      setSnack({ open: true, msg: err, sev: "error" });
      return;
    }

    setBusy(true);
    try {
      const token = await getTokenOrThrow();

      const payload: Draft & { id?: string } = {
        ...(selectedId ? { id: selectedId } : {}),
        ...draft,
        name: draft.name.trim(),
        location: draft.location.trim(),
        number: draft.number?.trim() || "",
        map: draft.map?.trim() || "",
        cuid: draft.cuid?.trim() || "",
        ruid: draft.ruid?.trim() || "",
        info: draft.info?.trim() || "",
        hours: (draft.hours ?? []).map((h) => h.trim()).filter(Boolean),
      };

      const saved = await upsertLocationMultipart(
        payload,
        { mapImage: mapFile, storeImage: storeFile },
        token
      );

      await refresh();
      setSelectedId(saved.id);

      // if backend returns signed urls, use them; otherwise keep current preview
      const anySaved = saved as any;
      if (anySaved.mapImgUrl) setMapPreview(anySaved.mapImgUrl);
      if (anySaved.storeImgUrl) setStorePreview(anySaved.storeImgUrl);

      // clear staged files after successful save
      setMapFile(null);
      setStoreFile(null);

      setSnack({ open: true, msg: selectedId ? "Saved" : "Created", sev: "success" });
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message ?? "Save failed", sev: "error" });
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!selectedId) return;
    if (!window.confirm("Delete this location?")) return;

    setBusy(true);
    try {
      const token = await getTokenOrThrow();
      await deleteLocation(selectedId, token);
      await refresh();
      startNew();
      setSnack({ open: true, msg: "Deleted", sev: "success" });
    } catch (e: any) {
      setSnack({ open: true, msg: e?.message ?? "Delete failed", sev: "error" });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "360px 1fr" }, gap: 2 }}>
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Locations</Typography>
            <Button variant="outlined" onClick={startNew}>
              New
            </Button>
          </Stack>

          <Divider sx={{ my: 2 }} />

          {error ? <Alert severity="error">{String(error)}</Alert> : null}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List dense sx={{ maxHeight: 560, overflow: "auto" }}>
              {(LOCATIONS as any[]).map((loc) => (
                <ListItemButton
                  key={loc.id}
                  selected={loc.id === selectedId}
                  onClick={() => setSelectedId(loc.id)}
                >
                  <ListItemText primary={loc.name} secondary={loc.location} />
                </ListItemButton>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Typography variant="h6">{selectedId ? "Edit Location" : "Add Location"}</Typography>
            <Stack direction="row" gap={1}>
              <Button variant="contained" onClick={onSave} disabled={busy}>
                Save
              </Button>
              <Button variant="outlined" color="error" onClick={onDelete} disabled={!selectedId || busy}>
                Delete
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} gap={2}>
              <TextField label="Name *" value={draft.name} onChange={(e) => set("name", e.target.value)} fullWidth />
              <TextField label="Number" value={draft.number ?? ""} onChange={(e) => set("number", e.target.value)} fullWidth />
            </Stack>

            <TextField
              label="Location *"
              value={draft.location}
              onChange={(e) => set("location", e.target.value)}
              fullWidth
            />

            <TextField
              label="Map link (optional)"
              value={draft.map ?? ""}
              onChange={(e) => set("map", e.target.value)}
              fullWidth
            />

            <Stack direction={{ xs: "column", md: "row" }} gap={2}>
              <TextField
                label="Latitude"
                value={draft.latitude ?? ""}
                onChange={(e) => set("latitude", toNumOrNull(e.target.value))}
                fullWidth
              />
              <TextField
                label="Longitude"
                value={draft.longitude ?? ""}
                onChange={(e) => set("longitude", toNumOrNull(e.target.value))}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} gap={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Map Image
                </Typography>
                <Button variant="outlined" component="label" disabled={busy}>
                  Choose Map Image
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;

                      // stage file; upload happens on Save
                      setMapFile(f);
                      setMapPreview(URL.createObjectURL(f));
                    }}
                  />
                </Button>

                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {mapFile ? `Selected: ${mapFile.name} (will upload on Save)` : "No new file selected."}
                </Typography>

                {mapPreview ? (
                  <Box
                    component="img"
                    src={mapPreview}
                    alt="Map preview"
                    sx={{ mt: 1, width: 260, borderRadius: 2, border: "1px solid", borderColor: "divider" }}
                  />
                ) : null}
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Store Image
                </Typography>
                <Button variant="outlined" component="label" disabled={busy}>
                  Choose Store Image
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;

                      setStoreFile(f);
                      setStorePreview(URL.createObjectURL(f));
                    }}
                  />
                </Button>

                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {storeFile ? `Selected: ${storeFile.name} (will upload on Save)` : "No new file selected."}
                </Typography>

                {storePreview ? (
                  <Box
                    component="img"
                    src={storePreview}
                    alt="Store preview"
                    sx={{ mt: 1, width: 260, borderRadius: 2, border: "1px solid", borderColor: "divider" }}
                  />
                ) : null}
              </Box>
            </Stack>

            <Divider />

            <Typography variant="subtitle1">Hours</Typography>
            <Stack direction="row" gap={1}>
              <TextField
                value={hoursInput}
                onChange={(e) => setHoursInput(e.target.value)}
                label='Add hours line (e.g. "Monday -  10:00am-9:00pm")'
                fullWidth
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addHour();
                  }
                }}
              />
              <Button variant="outlined" onClick={addHour}>
                Add
              </Button>
            </Stack>

            <Stack direction="row" gap={1} flexWrap="wrap">
              {draft.hours.map((h, idx) => (
                <Chip key={`${h}-${idx}`} label={h} onDelete={() => removeHour(idx)} />
              ))}
              {draft.hours.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No hours added.
                </Typography>
              ) : null}
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} gap={2}>
              <TextField label="CUID" value={draft.cuid ?? ""} onChange={(e) => set("cuid", e.target.value)} fullWidth />
              <TextField label="RUID" value={draft.ruid ?? ""} onChange={(e) => set("ruid", e.target.value)} fullWidth />
            </Stack>

            <TextField
              label="Info"
              value={draft.info ?? ""}
              onChange={(e) => set("info", e.target.value)}
              fullWidth
              multiline
              minRows={4}
            />
          </Stack>

          {busy ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Working…</Typography>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.sev} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}